import { useFeatureFlagContext } from 'restaurantAdmin/featureFlags';
import {
  SETTINGS_EDIT_TEAM_MEMBER_PATH,
  SETTINGS_FINANCES_PATH,
  SETTINGS_GENERAL_PATH,
  SETTINGS_INVITE_MEMBER_PATH,
  SETTINGS_LIGHTSPEED_PATH,
  SETTINGS_REPORTS_PATH,
  SETTINGS_TEAM_PATH,
  SETTINGS_VIP_PROGRAM_PATH,
} from 'restaurantAdmin/paths';
import cx from 'classnames';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import typography from '~styles/typography.scss';
import { PageContent } from '../layout/PageContent';
import { PageHeader } from '../layout/PageHeader';
import styles from './SettingsPage.scss';

const isTeamSectionPath = (pathname: string) =>
  pathname === SETTINGS_TEAM_PATH ||
  pathname.startsWith(`${SETTINGS_EDIT_TEAM_MEMBER_PATH}/`) ||
  pathname.startsWith(SETTINGS_INVITE_MEMBER_PATH);

const isLightspeedSectionPath = (pathname: string) =>
  pathname === SETTINGS_LIGHTSPEED_PATH ||
  pathname.startsWith(`${SETTINGS_LIGHTSPEED_PATH}/`);

export const SettingsPage = () => {
  const { pathname } = useLocation();
  const { isEnabled } = useFeatureFlagContext();

  // The location id is now chosen after connecting, so the page must be reachable without it.
  const showLightspeedNav = isEnabled('lightspeedIntegration');
  const showVipProgramNav =
    isEnabled('vipProgramCodes') || isEnabled('vipProgramTiers');

  return (
    <>
      <PageHeader title="Settings" />
      <PageContent
        className={cx('requires-unconstrained-layout', styles.content)}
      >
        <aside className={styles.sidebar}>
          <nav aria-label="Settings sections">
            <ul className={styles.navList}>
              <li>
                <NavLink
                  to={SETTINGS_GENERAL_PATH}
                  end
                  className={({ isActive }) =>
                    cx(typography.t1, styles.navItem, {
                      [styles.navItemActive]: isActive,
                    })
                  }
                >
                  General
                </NavLink>
              </li>
              {showVipProgramNav && (
                <li>
                  <NavLink
                    to={SETTINGS_VIP_PROGRAM_PATH}
                    end
                    className={({ isActive }) =>
                      cx(typography.t1, styles.navItem, {
                        [styles.navItemActive]: isActive,
                      })
                    }
                  >
                    VIP Program
                  </NavLink>
                </li>
              )}
              <li>
                <NavLink
                  to={SETTINGS_TEAM_PATH}
                  end
                  className={({ isActive }) =>
                    cx(typography.t1, styles.navItem, {
                      [styles.navItemActive]:
                        isActive || isTeamSectionPath(pathname),
                    })
                  }
                >
                  Team
                </NavLink>
              </li>
              <li>
                <NavLink
                  to={SETTINGS_FINANCES_PATH}
                  end
                  className={({ isActive }) =>
                    cx(typography.t1, styles.navItem, {
                      [styles.navItemActive]: isActive,
                    })
                  }
                >
                  Finances
                </NavLink>
              </li>
              <li>
                <NavLink
                  to={SETTINGS_REPORTS_PATH}
                  end
                  className={({ isActive }) =>
                    cx(typography.t1, styles.navItem, {
                      [styles.navItemActive]: isActive,
                    })
                  }
                >
                  Reports
                </NavLink>
              </li>
              {showLightspeedNav && (
                <li>
                  <NavLink
                    to={SETTINGS_LIGHTSPEED_PATH}
                    className={({ isActive }) =>
                      cx(typography.t1, styles.navItem, {
                        [styles.navItemActive]:
                          isActive || isLightspeedSectionPath(pathname),
                      })
                    }
                  >
                    Lightspeed
                  </NavLink>
                </li>
              )}
            </ul>
          </nav>
        </aside>
        <div className={styles.main}>
          <Outlet />
        </div>
      </PageContent>
    </>
  );
};
